import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Card, CardContent, Grid, TextField, Button, Typography, FormControl, InputLabel, Select, MenuItem, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton } from '@mui/material';
import { Save as SaveIcon, ArrowBack as BackIcon, Add as AddIcon, Delete as DeleteIcon, Send as SubmitIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

const TimesheetForm = ({ detailId: detailIdProp, embedded, onCloseEmbedded } = {}) => {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const id = detailIdProp ?? routeId;
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ timesheet_type: 'weekly', period_start: '', period_end: '', notes: '' });
  const [entries, setEntries] = useState([]);

  useEffect(() => { if (isEdit) fetchTimesheet(); }, [id]);

  const fetchTimesheet = async () => {
    setLoading(true);
    try { const r = await api.get(`/timesheets/${id}`); setFormData(r.data.data); setEntries(r.data.data.entries || []); }
    catch (e) {
      toast.error('Failed to fetch');
      if (embedded && typeof onCloseEmbedded === 'function') onCloseEmbedded();
      else navigate('/timesheets');
    }
    finally { setLoading(false); }
  };

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const addEntry = () => setEntries([...entries, { work_date: '', hours: 0, description: '', is_overtime: false }]);
  const updateEntry = (index, field, value) => { const newEntries = [...entries]; newEntries[index][field] = value; setEntries(newEntries); };
  const removeEntry = (index) => setEntries(entries.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) await api.put(`/timesheets/${id}`, { ...formData, entries });
      else await api.post('/timesheets', { ...formData, entries });
      toast.success(`Timesheet ${isEdit ? 'updated' : 'created'} successfully`);
      if (embedded && typeof onCloseEmbedded === 'function') onCloseEmbedded();
      else navigate('/timesheets');
    } catch (error) { toast.error(error.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleSubmitForApproval = async () => {
    try {
      await api.post(`/timesheets/${id}/submit`);
      toast.success('Submitted for approval');
      if (embedded && typeof onCloseEmbedded === 'function') onCloseEmbedded();
      else navigate('/timesheets');
    }
    catch (e) { toast.error('Failed to submit'); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  const totalHours = entries.reduce((sum, e) => sum + parseFloat(e.hours || 0), 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() =>
            embedded && typeof onCloseEmbedded === 'function' ? onCloseEmbedded() : navigate('/timesheets')
          }
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>{isEdit ? 'Edit Timesheet' : 'New Timesheet'}</Typography>
      </Box>
      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Timesheet Details</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth required><InputLabel>Type</InputLabel>
                  <Select name="timesheet_type" value={formData.timesheet_type} label="Type" onChange={handleChange} disabled={isEdit}>
                    <MenuItem value="daily">Daily</MenuItem><MenuItem value="weekly">Weekly</MenuItem><MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}><TextField fullWidth label="Period Start" name="period_start" type="date" value={formData.period_start?.split('T')[0] || ''} onChange={handleChange} InputLabelProps={{ shrink: true }} required disabled={isEdit} /></Grid>
              <Grid item xs={12} md={3}><TextField fullWidth label="Period End" name="period_end" type="date" value={formData.period_end?.split('T')[0] || ''} onChange={handleChange} InputLabelProps={{ shrink: true }} required disabled={isEdit} /></Grid>
              <Grid item xs={12} md={3}><Typography variant="h4" sx={{ textAlign: 'center', color: 'primary.main' }}>{totalHours.toFixed(1)} hrs</Typography></Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Time Entries</Typography>
              <Button startIcon={<AddIcon />} onClick={addEntry} disabled={formData.status && formData.status !== 'draft'}>Add Entry</Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Hours</TableCell><TableCell>Description</TableCell><TableCell>OT</TableCell><TableCell></TableCell></TableRow></TableHead>
                <TableBody>
                  {entries.length === 0 ? <TableRow><TableCell colSpan={5} align="center">No entries. Click "Add Entry" to add time.</TableCell></TableRow> :
                    entries.map((entry, i) => (
                      <TableRow key={i}>
                        <TableCell><TextField type="date" size="small" value={entry.work_date?.split('T')[0] || ''} onChange={(e) => updateEntry(i, 'work_date', e.target.value)} sx={{ width: 150 }} /></TableCell>
                        <TableCell><TextField type="number" size="small" value={entry.hours} onChange={(e) => updateEntry(i, 'hours', e.target.value)} sx={{ width: 80 }} inputProps={{ min: 0, max: 24, step: 0.5 }} /></TableCell>
                        <TableCell><TextField size="small" value={entry.description} onChange={(e) => updateEntry(i, 'description', e.target.value)} fullWidth /></TableCell>
                        <TableCell><input type="checkbox" checked={entry.is_overtime} onChange={(e) => updateEntry(i, 'is_overtime', e.target.checked)} /></TableCell>
                        <TableCell><IconButton size="small" onClick={() => removeEntry(i)} color="error"><DeleteIcon fontSize="small" /></IconButton></TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}><CardContent><TextField fullWidth label="Notes" name="notes" value={formData.notes || ''} onChange={handleChange} multiline rows={2} /></CardContent></Card>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() =>
              embedded && typeof onCloseEmbedded === 'function' ? onCloseEmbedded() : navigate('/timesheets')
            }
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} disabled={saving || (formData.status && formData.status !== 'draft')}>{saving ? 'Saving...' : 'Save'}</Button>
          {isEdit && formData.status === 'draft' && (<Button variant="contained" color="success" startIcon={<SubmitIcon />} onClick={handleSubmitForApproval}>Submit for Approval</Button>)}
        </Box>
      </form>
    </Box>
  );
};

export default TimesheetForm;
